/**
 * "The knowledge": minicab shifts for Kestrel Cars, booked from the phone box on Bell Street. A shift is a
 * run of fares; each one pays the meter, a tip for speed (against a par time a cabbie with the knowledge
 * would make) and a tip for care (no knocks, no dents, no police). Make the shift's target and Dot
 * Hennessy gives you the next, longer shift. Any car with a seat will do — except a police car.
 *
 * Fair with the law: nobody gets into a car the police are after (pick-ups wait for heat 0), and a
 * passenger who finds themselves in a chase (heat 2+) gets out at the next lights — that fare pays nothing.
 */
import { distance, type V2 } from '../core/math';
import type { MoodId } from '../render/mood';
import type { MissionScript } from '../missions/runner';
import type { MissionDef, RewardLine, StageDef } from '../missions/types';
import type { ActivityHost } from './host';
import { UNLOCK_STREET_JOBS } from '../missions/ids';
import { advanceTier, careTip, fareBase, farePar, fareTotal, recordHigh, shiftTarget, speedTip, type FareResult } from './scoring';
import { CAB_TIERS } from './state';

export const CAB_ID = 'cab-shift';
export const CAB_GIVER = 'phone-tanners';
export const CAB_FIRM = 'Kestrel Cars';
export const CAB_DISPATCH = 'Dot Hennessy';
/** Closing speed (m/s) that counts as a hard knock for the care tip, and the gap between two counted. */
export const HARD_HIT_SPEED = 6;
const HIT_GAP = 0.8;

export interface FareDef {
  from: string;
  fromName: string;
  to: string;
  toName: string;
  who: string;
  pickup: string;
  drop: string;
}

export interface ShiftDef {
  tier: number;
  title: string;
  mood: MoodId;
  fares: FareDef[];
}

export const CAB_SHIFTS: readonly ShiftDef[] = [
  {
    tier: 1,
    title: 'Afternoon Shift',
    mood: 'overcast',
    fares: [
      { from: 'pub-crooked-anchor', fromName: 'The Crooked Anchor', to: 'st-brannocs', toName: 'St. Brannoc’s', who: 'Mrs Ivy Platt', pickup: 'Evensong, love, and I’m late already.', drop: 'God bless you, dear.' },
      { from: 'mercy-square', fromName: 'Mercy Square', to: 'bank-savings', toName: 'the Savings Bank', who: 'The curate', pickup: 'The fete money wants banking before they shut.', drop: 'Lovely driving. I shall mention you in my prayers.' },
      { from: 'club-velvet-lamp', fromName: 'The Velvet Lamp', to: 'garage-halloran', toName: 'Halloran Motors', who: 'A drummer', pickup: 'Halloran’s, please. The band van’s in for a clutch.', drop: 'He’ll say Tuesday. It’s always Tuesday.' },
    ],
  },
  {
    tier: 2,
    title: 'Evening Shift',
    mood: 'dusk',
    fares: [
      { from: 'club-velvet-lamp', fromName: 'The Velvet Lamp', to: 'gasworks-yard', toName: 'the Gasworks Yard', who: 'A hostess', pickup: 'The gasworks. Don’t ask.', drop: 'You never saw me, sweetheart.' },
      { from: 'garage-halloran', fromName: 'Halloran Motors', to: 'police-station', toName: 'the police station', who: 'Mr Feltham, solicitor', pickup: 'The nick, and step on it. My client’s talking.', drop: 'Not a word, I hope. Keep the change.' },
      { from: 'bank-savings', fromName: 'the Savings Bank', to: 'mercy-square', toName: 'Mercy Square', who: 'An off-duty sergeant', pickup: 'Mercy Square. Keep it legal, son — I’m off duty, not blind.', drop: 'Decent driving. For a minicab.' },
      { from: 'st-brannocs', fromName: 'St. Brannoc’s', to: 'pub-crooked-anchor', toName: 'The Crooked Anchor', who: 'Two bell-ringers', pickup: 'The Anchor, mate. Thirsty work, bells.', drop: 'Cheers, drive! Coming in for one?' },
    ],
  },
  {
    tier: 3,
    title: 'Night Shift',
    mood: 'night',
    fares: [
      { from: 'club-velvet-lamp', fromName: 'The Velvet Lamp', to: 'docks-crane-quay', toName: 'the Basin Crane Quay', who: 'A sailor', pickup: 'The quay. My ship sails at midnight, with me or without.', drop: 'Just made it. Here, for your trouble.' },
      { from: 'gasworks-yard', fromName: 'the Gasworks Yard', to: 'scrapyard-gorringe', toName: 'Gorringe’s scrap yard', who: 'A man with a holdall', pickup: 'Gorringe’s. Don’t look in the bag.', drop: 'I was never in your car.' },
      { from: 'police-station', fromName: 'the police station', to: 'st-brannocs', toName: 'St. Brannoc’s', who: 'Old Mrs Marlowe', pickup: 'Visiting my grandson. St. Brannoc’s, love, and mind the bumps.', drop: 'Our Sid always said you were a nice boy.' },
      { from: 'mercy-square', fromName: 'Mercy Square', to: 'safehouse', toName: 'Pilcher Street', who: 'A night nurse', pickup: 'Pilcher Street. I’ve been on my feet since six.', drop: 'Bed. Finally. Thank you.' },
      { from: 'docks-bonded-3', fromName: 'the bonded warehouses', to: 'club-velvet-lamp', toName: 'The Velvet Lamp', who: 'A docker in his best suit', pickup: 'The Velvet Lamp. My girl’s singing tonight.', drop: 'Come in and hear her. On me.' },
    ],
  },
  {
    // Kiln Common (WP-X7): the long fares, out to the new streets and back across the borough.
    tier: 4,
    title: 'Saturday Shift',
    mood: 'dusk',
    fares: [
      { from: 'shop-lamb-walk', fromName: 'Lamb Walk Market', to: 'police-station', toName: 'the police station', who: 'A stallholder', pickup: 'The police station, love. Someone’s had my takings tin.', drop: 'And they’ll do nothing. You watch.' },
      { from: 'pub-brickmakers', fromName: 'the Brickmakers Arms', to: 'kiln-goods-depot', toName: 'the Kiln Road Goods Depot', who: 'A railwayman', pickup: 'Goods depot, mate. I’m on the night freight.', drop: 'Ta. Mind how you go.' },
      { from: 'kiln-goods-depot', fromName: 'the Kiln Road Goods Depot', to: 'st-brannocs', toName: 'St. Brannoc’s', who: 'A best man', pickup: 'St. Brannoc’s! I’ve got the ring and they started ten minutes ago!', drop: 'Made it! Don’t tell the bride.' },
      { from: 'st-brannocs', fromName: 'St. Brannoc’s', to: 'garage-kiln-motors', toName: 'Kiln Road Motors', who: 'Two wedding guests', pickup: 'Kiln Road Motors. Our car’s in for a new exhaust.', drop: 'Lovely. Send the bill to the groom.' },
      { from: 'lockup-coal-drop', fromName: 'Coal Drop Lane', to: 'club-velvet-lamp', toName: 'The Velvet Lamp', who: 'A bandleader', pickup: 'The Velvet Lamp. Doors at eight, and the drums are already there.', drop: 'There’s a table at the back if you want one.' },
    ],
  },
];

export function cabShift(tier: number): ShiftDef {
  return CAB_SHIFTS[Math.max(0, Math.min(CAB_SHIFTS.length - 1, tier - 1))]!;
}

/** Where each fare stops on the road and how long the drive is (metres along the roads). */
export interface FareGeometry {
  pickup: V2;
  drop: V2;
  metres: number;
}

const num = (m: MissionScript, k: string): number => (typeof m.vars[k] === 'number' ? (m.vars[k] as number) : 0);

/** "A drummer" mid-sentence is "a drummer"; "Mrs Ivy Platt" stays as she is. */
export function casual(who: string): string {
  return /^(A|An|The|Two) /.test(who) ? who.charAt(0).toLowerCase() + who.slice(1) : who;
}

/** Why the player can't take a fare on board yet, or null. */
function boardProblem(host: ActivityHost): string | null {
  const player = host.ctx.services.get('player');
  const v = player.mode === 'driving' ? player.vehicle : null;
  if (!v) return 'Your fare wants a car, not a piggyback. Get into a car first.';
  if (v.isPolice) return 'Nobody gets into a police car willingly. Use a different car.';
  if (v.spec.id === 'lorry') return 'A tipper lorry? Kestrel Cars has standards. Use a car.';
  if (host.ctx.state.heat > 0) return 'The police are after you. Lose them first — no fare gets into a car being chased.';
  return null;
}

function care(m: MissionScript): number {
  const max = num(m, 'fareMax') || 1;
  return Math.max(0, 1 - num(m, 'fareDamage') / max / 0.2 - num(m, 'fareHits') * 0.25);
}

/** A shift as a job. `geo` is one entry per fare (kerb stops and road distances). */
export function cabDef(host: ActivityHost, shift: ShiftDef, geo: readonly FareGeometry[]): MissionDef {
  const rec = () => host.record().cab;
  const n = shift.fares.length;
  const bases = geo.map((g) => fareBase(g.metres, shift.tier));
  const target = shiftTarget(bases);
  const meters = bases.reduce((a, b) => a + b, 0);
  const stages: StageDef[] = [];
  shift.fares.forEach((f, k) => {
    const g = geo[k]!;
    const i = k + 1;
    const base = bases[k]!;
    const par = farePar(g.metres, shift.tier);
    stages.push({
      id: `pickup-${i}`,
      kind: 'custom',
      at: { pos: g.pickup },
      radius: 6,
      text: `Fare ${i} of ${n}: pick up ${casual(f.who)} at ${f.fromName}.`,
      hint: 'Pull up at the kerb by the marker and stop. They’ll get in.',
      blocked: (m) => distance(m.ctx.services.get('player').pos, g.pickup) < 16 && boardProblem(host) !== null,
      blockedText: () => boardProblem(host) ?? '',
      done: (m) => {
        const player = m.ctx.services.get('player');
        return player.mode === 'driving' && !!player.vehicle && player.vehicle.speed < 1.5 && distance(player.pos, g.pickup) < 9 && boardProblem(host) === null;
      },
      onTick: (m) => host.ticket({ head: `FARE ${i} OF ${n}`, lines: [`Taken so far £${num(m, 'taken')}`, `Shift target £${target}`] }),
      onComplete: (m) => {
        const v = m.ctx.services.get('player').vehicle!;
        m.vars.fareStart = m.ctx.state.time;
        m.vars.fareCar = v.id;
        m.vars.fareMax = v.spec.maxHealth;
        m.vars.fareDamage = 0;
        m.vars.fareHits = 0;
        m.vars.fareLaw = false;
        m.vars.fareBailed = false;
        m.vars.lastHit = -10;
        m.say([{ who: f.who, text: f.pickup }]);
      },
    });
    stages.push({
      id: `ride-${i}`,
      kind: 'custom',
      at: { pos: g.drop },
      radius: 6,
      text: `Take ${casual(f.who)} to ${f.toName}.`,
      hint: 'Fast earns a tip, careful earns another. Crashes cost tips; a police chase loses the fare.',
      blocked: (m) => m.ctx.services.get('player').mode !== 'driving',
      blockedText: 'Get back in the car — your fare is waiting in the back.',
      done: (m) => {
        if (m.vars.fareBailed === true) return true;
        const player = m.ctx.services.get('player');
        return player.mode === 'driving' && !!player.vehicle && player.vehicle.speed < 2 && distance(player.pos, g.drop) < 9;
      },
      onEnter: (m) => {
        const car = num(m, 'fareCar');
        m.listen('collision', (e) => {
          const hit = (e.a.kind === 'vehicle' && e.a.id === car) || (e.b?.kind === 'vehicle' && e.b.id === car);
          if (!hit || !e.isNew || e.speed < HARD_HIT_SPEED) return;
          if (m.ctx.state.time - num(m, 'lastHit') < HIT_GAP) return;
          m.vars.lastHit = m.ctx.state.time;
          m.vars.fareHits = num(m, 'fareHits') + 1;
          if (num(m, 'fareHits') === 1) m.say([{ who: f.who, text: 'Oi! Steady on!', seconds: 1.6 }]);
        });
        m.listen('vehicleDamaged', (e) => {
          if (e.vehicleId === car) m.vars.fareDamage = num(m, 'fareDamage') + e.amount;
        });
        m.listen('vehicleDestroyed', (e) => {
          if (e.vehicleId === car) m.fail(`${f.who} got out of a burning car and is ringing Kestrel Cars about it. Shift over — try again and drive more carefully.`);
        });
      },
      onTick: (m) => {
        const heat = m.ctx.state.heat;
        if (heat > 0) m.vars.fareLaw = true;
        if (heat >= 2 && m.vars.fareBailed !== true) {
          m.vars.fareBailed = true;
          m.say([{ who: f.who, text: 'I’m not getting nicked for a cab fare! Let me out!' }]);
          m.toast(`${f.who} got out. No fare.`, 'bad');
          return;
        }
        const t = m.ctx.state.time - num(m, 'fareStart');
        const stars = Math.round(care(m) * 3);
        host.ticket({
          head: `FARE ${i} OF ${n}`,
          lines: [`Meter £${base}`, `${Math.floor(t)} s · par ${Math.round(par)} s`, `Care ${'★'.repeat(stars)}${'·'.repeat(3 - stars)}${m.vars.fareLaw === true ? ' · police seen' : ''}`],
        });
      },
      onComplete: (m) => {
        const t = m.ctx.state.time - num(m, 'fareStart');
        const res: FareResult = {
          base,
          speed: speedTip(base, t, par),
          care: careTip(base, num(m, 'fareDamage') / (num(m, 'fareMax') || 1), num(m, 'fareHits'), m.vars.fareLaw === true),
          bailed: m.vars.fareBailed === true,
        };
        m.vars[`fare${i}`] = fareTotal(res);
        m.vars[`fare${i}tips`] = res.bailed ? 0 : res.speed + res.care;
        m.vars[`fare${i}bailed`] = res.bailed;
        m.vars.taken = num(m, 'taken') + fareTotal(res);
        if (!res.bailed) m.say([{ who: f.who, text: f.drop }]);
      },
    });
  });
  return {
    id: CAB_ID,
    title: shift.title,
    client: CAB_DISPATCH,
    tag: `MINICAB · ${CAB_FIRM.toUpperCase()} · SHIFT ${shift.tier} OF ${CAB_TIERS}`,
    blurb: `Minicab work for ${CAB_FIRM}: drive ${n} passengers around the borough in any car. Each pays the meter, plus tips for a fast, careful ride. Earn £${target} in one shift and Dot gives you longer shifts.`,
    // What the meters alone pay (the card's stamp); tips come on top, the target needs them.
    reward: meters,
    requires: [],
    requiresUnlock: UNLOCK_STREET_JOBS,
    repeatable: true,
    mood: shift.mood,
    giver: { poi: CAB_GIVER, prompt: `Ring ${CAB_FIRM} — a minicab shift`, glyph: 'C', whileDriving: true },
    activity: {
      kicker: `Minicab · shift ${shift.tier} of ${CAB_TIERS}`,
      meta: () => {
        const best = rec().best[shift.tier - 1] ?? 0;
        return `${n} fares, £${meters} on the meters. Target £${target}. ${best > 0 ? `Your best shift: £${best}.` : 'Not driven yet.'}`;
      },
      tried: () => rec().runs > 0,
    },
    intro: [
      { who: CAB_DISPATCH, text: `${CAB_FIRM}, Dot speaking. ${shift.title}: ${n} fares, first one’s at ${shift.fares[0]!.fromName}.` },
      { who: CAB_DISPATCH, text: 'Quick and careful, love. They tip for both. And no police — they don’t tip for that.' },
    ],
    introFocus: { pos: geo[0]!.pickup },
    setup: () => host.ticket(null),
    stages,
    outro: [{ who: CAB_DISPATCH, text: 'That’s your shift. Bring the car back in one piece next time — oh, it’s yours. Never mind.' }],
    payout: (m) => {
      const lines: RewardLine[] = [];
      shift.fares.forEach((f, k) => {
        const i = k + 1;
        if (m.vars[`fare${i}bailed`] === true) lines.push({ label: `${f.who}: got out, no fare`, amount: 0 });
        else lines.push({ label: `${f.who}: meter £${bases[k]} + tips £${num(m, `fare${i}tips`)}`, amount: num(m, `fare${i}`) });
      });
      return lines;
    },
    resultStamp: 'SHIFT DONE',
    resultLine: (m) => {
      const taken = num(m, 'taken');
      if (taken >= target) return `£${taken} taken — over the £${target} target. ${shift.tier < CAB_TIERS ? 'Dot’s got a longer shift for you.' : 'Best cabbie in the borough.'}`;
      return `£${taken} taken. Dot wants £${target} off a shift before the better work comes your way.`;
    },
    onResolved: (m, outcome) => {
      host.ticket(null);
      if (outcome !== 'completed') {
        if (typeof m.vars.fareStart === 'number') {
          advanceTier(rec(), shift.tier, false, CAB_TIERS);
          host.dirty();
        }
        return;
      }
      const r = rec();
      const taken = num(m, 'taken');
      recordHigh(r, shift.tier, taken);
      const { unlockedNew } = advanceTier(r, shift.tier, taken >= target, CAB_TIERS);
      host.dirty();
      if (unlockedNew) announceShift(host, unlockedNew);
    },
  };
}

/** Book note for a new shift (and the first one). */
export function announceShift(host: ActivityHost, n: number): void {
  const giver = host.ctx.services.get('layout').pois.find((p) => p.id === CAB_GIVER);
  const s = cabShift(n);
  host.post({
    key: `cab-shift:${n}`,
    from: CAB_DISPATCH,
    kind: 'offer',
    subject: n === 1 ? 'Drivers wanted' : `The ${s.title.toLowerCase()} is yours`,
    text:
      n === 1
        ? `${CAB_FIRM} needs drivers. Bring your own motor, we find the fares. Meter money is yours, and the tips if you’re quick and careful.\n\nRing us from the phone box on Bell Street.`
        : `You took your target, so the ${s.title.toLowerCase()} is yours: ${s.fares.length} fares, better money. Same phone box.`,
    ...(giver ? { action: { label: 'Mark the phone box on the map', mark: { x: giver.pos.x, z: giver.pos.z, label: `${CAB_FIRM} — Bell Street` } } } : {}),
  });
}
